import { COMPANY_NAME, JURISDICTION, EFFECTIVE_DATE, LEGAL_CONTACT_EMAIL } from '../config/legal'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { FileText, Shield, CreditCard, AlertCircle, Scale } from 'lucide-react'
import BackgroundBeams from '@/components/aceternity/BackgroundBeams'
import ScrollReveal from '@/components/aceternity/ScrollReveal'

const Terms = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <BackgroundBeams />
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-10 md:pt-24 md:pb-16 text-center">
          <ScrollReveal className="flex flex-col items-center gap-4">
            <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <FileText className="w-7 h-7 md:w-8 md:h-8" />
            </div>
            <Badge variant="secondary" className="bg-primary/15 text-primary border-primary/20">Legal</Badge>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
              Terms of Service
            </h1>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
              Effective Date: {EFFECTIVE_DATE}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <Separator className="bg-border/60" />

      {/* Introduction */}
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Agreement to Terms</CardTitle>
              <CardDescription>
                {COMPANY_NAME} ("Kinora", "we", "us") • Contact: {LEGAL_CONTACT_EMAIL}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm md:text-base text-muted-foreground space-y-3">
              <p>
                By creating an account or using Kinora (the "Service"), you agree to these Terms and our Privacy Policy.
                If you don't agree, don't use the Service.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Eligibility & Accounts */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-primary" /> Eligibility
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  You must be at least 13 years old to use the Service. If you are under the age of majority in your
                  jurisdiction, you represent that you have permission from a parent/guardian.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-primary" /> Accounts and Sign-In
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>
                  You agree to provide accurate account information and keep it updated. You are responsible for
                  activity under your account.
                </p>
                <p>
                  Kinora may offer sign-in through third parties (e.g., Google/Apple). Your use of those sign-in
                  options is also subject to their terms.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Platform & License */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>What Kinora Is (Platform Status)</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                Kinora is a technology platform that hosts and streams content provided by third-party creators/publishers.
                Kinora does not create the content and does not endorse it. Featured sections, rankings, and recommendations
                are automated and/or curated for discovery and do not constitute endorsement.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>License to Use the Service</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p>
                Kinora grants you a limited, non-exclusive, non-transferable, revocable license to access and use the
                Service for personal, non-commercial viewing, subject to these Terms.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Restrictions */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted border-destructive/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-destructive" /> Restrictions
              </CardTitle>
              <CardDescription>You may not:</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Copy, download, rip, redistribute, publicly perform, resell, or exploit content except where explicitly allowed</li>
                <li>Circumvent access controls, DRM, paywalls, or geo-restrictions</li>
                <li>Reverse-engineer, probe, scan, or disrupt the Service</li>
                <li>Use bots/scrapers to access the Service or content at scale</li>
                <li>Use the Service to violate laws or third-party rights</li>
              </ul>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Billing */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" /> Subscriptions, Billing, and No-Refund Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p className="mb-3">If you purchase a subscription or paid features:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Billing may be recurring and will renew automatically until you cancel.</li>
                <li><strong>No refunds once billed</strong>, except where required by law.</li>
                <li>If you cancel, you keep access until the end of the current billing period, and the subscription will not renew.</li>
                <li>Payments are handled by third-party processors. Kinora does not store full payment card details.</li>
              </ul>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Slices */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>"Slices" and Virtual Units</CardTitle>
              <CardDescription>Kinora may include virtual units (e.g., "Slices"). You agree that:</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Slices are not currency, have no cash value, and are not redeemable for money.</li>
                <li>Slices reset monthly and do not roll over (no banking).</li>
                <li>Slices may be available through subscription and/or purchase (if offered).</li>
                <li>Slices are non-transferable between users and cannot be sold or traded.</li>
                <li>Kinora may modify slice mechanics (including reset rules) with notice where practical.</li>
              </ul>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* User Interactions & Moderation */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="text-lg">User Interactions</CardTitle>
                <CardDescription>Current Viewer Mode; Future Comments</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  Right now, accounts are intended for viewing and platform usage. If Kinora later enables comments
                  or other user-generated content features, additional rules and moderation policies may apply, and
                  Kinora may remove content or restrict features at its discretion.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <Card className="bg-card/70 border-muted h-full">
              <CardHeader>
                <CardTitle className="text-lg">Moderation and Enforcement</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>
                  Kinora may take enforcement actions based on severity and risk, including warnings, feature limits,
                  suspensions, and termination.
                </p>
                <p>
                  Kinora maintains a strict stance against illegal activity, piracy, harassment, doxxing, threats,
                  malware, fraud, and similar abuse. Some categories (including hate content) may be handled case-by-case
                  by moderators.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Copyright */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <ScrollReveal>
          <Card className="bg-card/70 border-muted">
            <CardHeader>
              <CardTitle>Copyright and Takedowns</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
              <p>
                If you believe content on Kinora infringes your copyright, contact: <strong>{LEGAL_CONTACT_EMAIL}</strong>
              </p>
              <p>
                Include: identification of the work, the allegedly infringing material/location, your contact info,
                and a good-faith statement of infringement.
              </p>
            </CardContent>
          </Card>
        </ScrollReveal>
      </section>

      {/* Service & Legal */}
      <section className="max-w-5xl mx-auto px-6 pb-10 md:pb-14">
        <div className="space-y-6">
          <ScrollReveal>
            <Card className="bg-card/70 border-muted">
              <CardHeader>
                <CardTitle className="text-lg">Service Availability and Changes</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  The Service may change over time. Kinora may add/remove features, content, or supported devices,
                  and may suspend the Service for maintenance, security, or operational reasons.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={80}>
            <Card className="bg-card/70 border-muted">
              <CardHeader>
                <CardTitle className="text-lg">Disclaimers</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  The Service is provided "as is" and "as available." Kinora disclaims warranties to the maximum extent
                  permitted by law, including fitness for a particular purpose, availability, and non-infringement.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={160}>
            <Card className="bg-card/70 border-muted">
              <CardHeader>
                <CardTitle className="text-lg">Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  To the maximum extent permitted by law, Kinora will not be liable for indirect, incidental, consequential,
                  special, or punitive damages, or for loss of profits, data, goodwill, or business interruption.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={240}>
            <Card className="bg-card/70 border-muted">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Scale className="w-5 h-5 text-primary" /> Governing Law
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  These Terms are governed by the laws of {JURISDICTION}, without regard to conflict of laws principles.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={320}>
            <Card className="bg-card/70 border-muted">
              <CardHeader>
                <CardTitle className="text-lg">Changes to These Terms</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  Kinora may update these Terms. If changes are material, Kinora will provide reasonable notice
                  (e.g., via the Service). Continued use after changes means you accept the updated Terms.
                </p>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}

export default Terms
